'use client';

import { useTranslations } from 'next-intl';
import { CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_ENDPOINTS } from '@/lib/api/config';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import type { QrReviewPageRecord } from '../types';

// ─── Props ───────────────────────────────────────────────────────────────────

interface QrReviewPageSidebarProps {
  pages: QrReviewPageRecord[];
  selectedPageIndex: number;
  onSelectPage: (index: number) => void;
  batchId: number;
  progressPct: number;
  /** Overrides the fixed `w-64` — passed `w-full` when embedded in a Sheet. */
  className?: string;
}

// ─── ThumbnailImage ──────────────────────────────────────────────────────────

function ThumbnailImage({
  batchId,
  pageId,
  qaVerdict
}: {
  batchId: number;
  pageId: number;
  qaVerdict: string | null;
}) {
  const blobUrl = useAuthenticatedBlobUrl(
    API_ENDPOINTS.PHOTO_SCANNING.PAGE_THUMBNAIL(batchId, pageId)
  );

  const overlay =
    qaVerdict === 'passed' ? (
      <div className='absolute inset-0 flex items-center justify-center rounded bg-green-500/20'>
        <CheckCircle2 className='h-6 w-6 text-green-600' />
      </div>
    ) : qaVerdict === 'rejected' ? (
      <div className='absolute inset-0 flex items-center justify-center rounded bg-red-500/20'>
        <XCircle className='h-6 w-6 text-red-600' />
      </div>
    ) : null;

  if (!blobUrl) {
    return (
      <div className='bg-muted relative h-16 w-11 flex-shrink-0 rounded'>
        {overlay}
      </div>
    );
  }

  return (
    <div className='relative h-16 w-11 flex-shrink-0 overflow-hidden rounded'>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={blobUrl} alt='' className='h-full w-full object-cover' />
      {overlay}
    </div>
  );
}

// ─── QrReviewPageSidebar ─────────────────────────────────────────────────────

export function QrReviewPageSidebar({
  pages,
  selectedPageIndex,
  onSelectPage,
  batchId,
  progressPct,
  className
}: QrReviewPageSidebarProps) {
  const t = useTranslations('qrReview');

  return (
    <aside
      className={cn('flex w-64 flex-shrink-0 flex-col border-e', className)}
    >
      {/* Header */}
      <div className='flex items-center justify-between border-b px-4 py-3'>
        <span className='text-muted-foreground text-xs font-semibold tracking-wide uppercase'>
          {t('pageTitle')}
        </span>
        <span className='text-muted-foreground text-xs'>
          {progressPct}% {t('metricCompleted')}
        </span>
      </div>

      {/* Pages list */}
      <div className='flex-1 overflow-y-auto py-2'>
        {pages.map((page, index) => (
          <button
            key={page.id}
            type='button'
            onClick={() => onSelectPage(index)}
            className={cn(
              'hover:bg-muted/50 flex w-full items-center gap-3 border-s-2 px-3 py-2 transition-colors',
              selectedPageIndex === index
                ? 'border-s-blue-500 bg-blue-50/50'
                : 'border-s-transparent'
            )}
          >
            <ThumbnailImage
              batchId={batchId}
              pageId={page.id}
              qaVerdict={page.qa_verdict}
            />
            <div className='min-w-0 flex-1 text-start'>
              <div className='truncate text-sm font-medium'>
                {t('pageLabel')} {String(page.sequence).padStart(2, '0')}
              </div>
              <div
                className={cn(
                  'text-xs font-semibold',
                  page.qa_verdict === 'passed'
                    ? 'text-green-600'
                    : page.qa_verdict === 'rejected'
                      ? 'text-red-600'
                      : 'text-blue-600'
                )}
              >
                {page.qa_verdict === 'passed'
                  ? t('statusQaPassed').toUpperCase()
                  : page.qa_verdict === 'rejected'
                    ? t('statusQaFailed').toUpperCase()
                    : t('statusReviewing')}
              </div>
            </div>
          </button>
        ))}
      </div>
    </aside>
  );
}
